/**
 * ASCENSION — the single shared source for the 5-gear × 3-tier DMT roster.
 *
 * Each ORDER gear holds 3 skillsets across 3 ascension tiers. The DEMI-tier
 * seat is the season-1 starting five (ORACLE, NUMA, KYRS, METIS, CLIO); TYTN and
 * OLMPN are additive. Nothing is locked for use. Every other file imports its gear/tier roster from HERE — no
 * duplicated rosters. See docs/OLMPS_V9_SPEC (new-system conformance spec).
 *
 * Ascensions: MORTAL = baseline · DEMI = 5 secured · TYTN = 10 · OLMPN = 15
 * (the app's goal — display the stylized "OLMPN" where the goal is named).
 * Internal tier ids (mortal/titan/olympian) remain unchanged for persistence.
 */
import type { DmtToolId } from './spectrum-tools';

export type GearId = 'orient' | 'regulate' | 'direct' | 'execute' | 'revise';
export type TierId = 'mortal' | 'titan' | 'olympian';

export const TIER_ORDER: TierId[] = ['mortal', 'titan', 'olympian'];

export const TIER_LABEL: Record<TierId, string> = {
  mortal: 'DEMI',
  titan: 'TYTN',
  olympian: 'OLMPN',
};

export const GEAR_TIERS: Record<GearId, Record<TierId, DmtToolId>> = {
  orient: { mortal: 'oracle', titan: 'telos', olympian: 'astra' },
  regulate: { mortal: 'numa', titan: 'moyri', olympian: 'hrmez' },
  direct: { mortal: 'kyrs', titan: 'krnos', olympian: 'tkne' },
  execute: { mortal: 'metis', titan: 'themis', olympian: 'janus' },
  revise: { mortal: 'clio', titan: 'argos', olympian: 'nemos' },
};

/** The DEMI seat for each gear — the season-1 starting five. Every tool is usable from day one; only scoring is gated. */
export const GEAR_ANCHOR: Record<GearId, DmtToolId> = {
  orient: GEAR_TIERS.orient.mortal,
  regulate: GEAR_TIERS.regulate.mortal,
  direct: GEAR_TIERS.direct.mortal,
  execute: GEAR_TIERS.execute.mortal,
  revise: GEAR_TIERS.revise.mortal,
};

/**
 * Ascension rank from the count of secured DMTs (out of the canonical 15).
 * MORTAL = baseline (survival instinct, 0–4 secured); DEMI = 5+; TYTN = 10+; OLMPN = 15.
 */
export function ascensionRank(securedCount: number): 'MORTAL' | 'DEMI' | 'TYTN' | 'OLMPN' {
  if (securedCount >= 15) return 'OLMPN';
  if (securedCount >= 10) return 'TYTN';
  if (securedCount >= 5) return 'DEMI';
  return 'MORTAL';
}

/** Which gear + tier a given DMT tool id belongs to, or null if not in the roster. */
export function tierOf(tool: DmtToolId): { gear: GearId; tier: TierId } | null {
  for (const gear of Object.keys(GEAR_TIERS) as GearId[]) {
    for (const tier of TIER_ORDER) {
      if (GEAR_TIERS[gear][tier] === tool) return { gear, tier };
    }
  }
  return null;
}
